import { Cart } from "@/lib/shopify/types";
import { trackMetaEvent } from "@/lib/meta-pixel";
import { multiCopyCents } from "@/lib/comix/bagConfig";

const DIGITAL_IN_PHYSICAL_MSG =
  "You've already added the printed book of this comic, which includes the digital copy for free. Add the digital version anyway?";

/** Dispatched on `window` with the fresh cart whenever a line is successfully added
 *  from outside the bag drawer's own tree (e.g. /comix/unlock, /comix/checkout,
 *  /comix/stories) — see BagDrawer's listener for why this replaces routing the add
 *  through a Server Action + revalidation. */
export const BAG_UPDATED_EVENT = "printwithmagic:bag-updated";

type AddComixToBagResult =
  | { status: "added"; cart?: Cart }
  | { status: "error"; message: string }
  | { status: "needs_confirm"; reason: "digital_included_in_physical" };

async function postAddToBag(payload: {
  variantId: string;
  attributes: { key: string; value: string }[];
  force?: boolean;
  quantity?: number;
}): Promise<AddComixToBagResult> {
  try {
    const res = await fetch("/api/cart/add", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return { status: "error", message: "Error adding item to bag" };
    return await res.json();
  } catch {
    return { status: "error", message: "Error adding item to bag" };
  }
}

/**
 * Client-side wrapper around POST /api/cart/add that surfaces the "digital already
 * included in your printed book" confirmation. Re-calls with force after the shopper
 * confirms.
 *
 * Returns `added: true` when a line was added/incremented, `added: false` when
 * the shopper cancelled the confirmation, and an `error` string on failure.
 */
export async function addComixToBagWithConfirm(payload: {
  variantId: string;
  attributes: { key: string; value: string }[];
  quantity?: number;
}): Promise<{ added: boolean; error?: string }> {
  // One add in flight at a time: callers guard with React state, which only updates on
  // the next render, so a fast double-click could add twice (and fire two AddToCarts).
  if (addInFlight) return { added: false };
  addInFlight = true;
  try {
    return await addComixToBagOnce(payload);
  } finally {
    addInFlight = false;
  }
}

let addInFlight = false;

async function addComixToBagOnce(payload: {
  variantId: string;
  attributes: { key: string; value: string }[];
  quantity?: number;
}): Promise<{ added: boolean; error?: string }> {
  let result = await postAddToBag(payload);

  if (result.status === "needs_confirm") {
    if (typeof window !== "undefined" && window.confirm(DIGITAL_IN_PHYSICAL_MSG)) {
      result = await postAddToBag({ ...payload, force: true });
    } else {
      return { added: false };
    }
  }

  if (result.status === "error") return { added: false, error: result.message };

  if (result.status === "added" && result.cart && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BAG_UPDATED_EVENT, { detail: result.cart }));
  }

  if (result.status === "added") {
    // Value comes from the line Shopify just priced, so bundles/discounts stay accurate.
    // Copies added this time: the 1st at full price, the rest at the 2nd-book price.
    const line = result.cart?.lines.find((l) => l.merchandise.id === payload.variantId);
    const unit = line ? parseFloat(line.cost.amountPerQuantity.amount) : undefined;
    const copies = Math.max(1, payload.quantity ?? 1);
    trackMetaEvent("AddToCart", {
      content_ids: [payload.variantId],
      content_name: line?.merchandise.product?.title,
      value: unit !== undefined ? multiCopyCents(Math.round(unit * 100), copies) / 100 : undefined,
      currency: line?.cost.amountPerQuantity.currencyCode,
      num_items: copies,
      contents: unit !== undefined ? [{ id: payload.variantId, quantity: copies, item_price: unit }] : undefined,
    });
  }

  return { added: result.status === "added" };
}
